/**
 * Cozy coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const cozyColoringPack: ToilePack = {
    slug: 'cozy-coloring',
    category: 'coloring',
    name: 'Cozy coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'window', label: 'Rainy window', subject: 'a cat asleep on a window seat cushion, rain on the panes, a mug and a folded book beside it'},
        {key: 'nook', label: 'Reading nook', subject: 'a deep armchair with a knitted blanket over its arm, a standing lamp and a leaning stack of books'},
        {key: 'kitchen', label: 'Kitchen', subject: 'a kitchen counter, a steaming kettle, a frosted cake under a glass dome and a potted herb on the sill'},
    ],
    styles: [
        {key: 'simple', label: 'Simple', style: 'gentle beginner cozy line art, rounded outlines of even medium weight, few objects, large calm areas left open', palette: 'black ink only', framing: 'The scene centered with a generous white margin'},
        {key: 'detailed', label: 'Detailed', style: 'medium-fine even outlines, the knit, the rug and the wood grain each given their own drawn texture', palette: 'black ink only', framing: "The scene filling the full page to the paper's edge"},
        {key: 'vignette', label: 'Vignette', style: 'a soft round vignette, the scene fading into an ornamental ring of leaves and stars, rounded even lines', palette: 'black ink only', framing: 'The drawing set inside a circle high on the page, quiet white paper outside'},
    ],
    directions: ['a fireplace instead of the window', 'in December with a small tree', 'with a dog instead of a cat'],
    seo: {
        title: 'Cozy coloring pages: rainy window, nook, kitchen',
        metaDescription: 'Nine cozy coloring pages — a rainy window, a reading nook, a kitchen — drawn simple, detailed or as a soft vignette, ready to print.',
        h1: 'Cozy coloring pages: rainy window, reading nook, kitchen in three styles',
        tagline: 'Nine cozy corners, three rooms, three styles each — for the evening you color with tea and a blanket.',
        intro: 'Cozy is the register that took over adult coloring: a cat asleep on a rainy windowsill, a reading nook with a lamp, a kitchen with a kettle and a cake. These nine are drawn that way — rounded lines, closed shapes, nothing sharp — simple for a first sitting, detailed for a slow one, or set inside a soft vignette. Print the page, or ask for the same room with your own twist.',
        useCases: [
            'a quiet evening wind-down',
            'a bedside coloring stack',
            'a gift card insert for a tea-lover',
            'a slow Sunday activity',
            "a seniors' group coloring circle",
        ],
        faq: [
            {q: 'What size do the pages print at?', a: 'Each page is drawn at portrait A4 proportions (832 by 1216) with a white margin, so it prints clean on standard paper.'},
            {q: 'Are the lines closed, so color stays inside?', a: 'Yes — every shape is a closed black outline on a pure white page, no gray fills, no text, no shading.'},
            {q: 'Can I get the same room with a different animal or season?', a: 'Yes — that is what the direction field on the page is for. Type a different twist and the room is drawn again in the same three styles.'},
        ],
    },
}
